import React, { useState, useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PartySearch from "../../components/PartySearch";
import PartySummary from "../../components/PartySummary";
import {
  Party,
  RegistrationStatus,
  SelectedSession,
  ServiceGroupDto,
} from "../../types/types";
import { notify } from "../../components-ui/toast";
import { api } from "../../../api/apiClient";
import "../../../css/buttons.css";
import "../../../css/forms.css";

interface RegisterPartyToInstanceTabProps {
  session: SelectedSession | null;
  selectedParty: Party | null;
  onSelectedPartyChange: (party: Party | null) => void;
  returnedPartyId?: number | null;
  loading: boolean;
  onRegistered?: (count: number) => void;
  onAddPerson: () => void;
  onEditPerson: (partyId: number) => void;
}

interface ServiceInstanceRegistrationResponse {
  serviceInstanceId: number;
  partyId: number;
  registeredCount: number;
}

export default function RegisterPartyToInstanceTab({
  session,
  selectedParty,
  onSelectedPartyChange,
  returnedPartyId,
  loading,
  onRegistered,
  onAddPerson,
  onEditPerson,
}: RegisterPartyToInstanceTabProps) {
  const [registrationStatus, setRegistrationStatus] =
    useState<RegistrationStatus | null>(null);
  const [serviceGroups, setServiceGroups] = useState<ServiceGroupDto[]>([]);
  const [selectedServiceGroupId, setSelectedServiceGroupId] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(false);

  const serviceInstanceId = session?.serviceInstanceId ?? null;
  const activeServiceGroups = serviceGroups.filter((group) => group.isActive);

  /* =========================
   * After the PartySummary loads the returned party, clear the returned ID.
   * ========================= */

  useEffect(() => {
    if (!returnedPartyId) return;

    onSelectedPartyChange({ partyId: returnedPartyId } as Party);
  }, [onSelectedPartyChange, returnedPartyId]);

  useEffect(() => {
    setSelectedServiceGroupId("");

    if (!session?.serviceId) {
      setServiceGroups([]);
      return;
    }

    api<ServiceGroupDto[]>(
      `/api/services/${session.serviceId}/groups?includeInactive=false`,
    )
      .then((groups) => setServiceGroups(groups ?? []))
      .catch(() => setServiceGroups([]));
  }, [session?.serviceId]);

  /* =========================
   * Check registration status
   * ========================= */
  useEffect(() => {
    if (!selectedParty || !serviceInstanceId) {
      setRegistrationStatus(null);
      return;
    }

    setCheckingStatus(true);

    api<RegistrationStatus>(
      `/api/serviceinstances/${serviceInstanceId}/registrations/${selectedParty.partyId}/status`,
    )
      .then(setRegistrationStatus)
      .catch(() => notify.error("Failed to check registration status"))
      .finally(() => setCheckingStatus(false));
  }, [selectedParty, serviceInstanceId]);

  /* =========================
   * Register party
   * ========================= */
  const registerParty = async (partyId: number) => {
    if (!serviceInstanceId) return;

    try {
      const response = await api<ServiceInstanceRegistrationResponse>(
        `/api/serviceinstances/${serviceInstanceId}/registrations`,
        {
          method: "POST",
          body: {
            partyId,
            serviceGroupId: selectedServiceGroupId
              ? Number(selectedServiceGroupId)
              : null,
          },
        },
      );

      notify.success("Party successfully registered");

      // Pass updated count to parent
      onRegistered?.(response.registeredCount);

      // Clear selection so next registration can happen
      onSelectedPartyChange(null);
      setSelectedServiceGroupId("");
      setRegistrationStatus(null);
    } catch {
      notify.error("Failed to register party");
    }
  };

  const handleRegister = async () => {
    if (!serviceInstanceId || !selectedParty) return;
    await registerParty(selectedParty.partyId);
  };

  return (
    <div className="card">
      <div className="form-row party-search-row">
        <div className="form-group" style={{ flex: 1 }}>
          <PartySearch
            onPartySelected={(party) => {
              onSelectedPartyChange(party);
              setRegistrationStatus(null);
            }}
          />
        </div>
      </div>

      {!selectedParty && (
        <div className="registration-add-prompt">
          <span>Can’t find them?</span>
          <button type="button" onClick={onAddPerson}>
            <PersonAddIcon fontSize="small" />
            Add New
          </button>
        </div>
      )}

      {/* Party Summary */}
      {selectedParty && (
        <div className="registration-selected-party">
          <PartySummary partyId={selectedParty.partyId} />
          <button
            type="button"
            className="registration-edit-action"
            onClick={() => onEditPerson(selectedParty.partyId)}
          >
            <EditIcon fontSize="small" />
            Edit details
          </button>
        </div>
      )}

      {activeServiceGroups.length > 0 && (
        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Group / Class</label>
            <select
              className="form-input"
              value={selectedServiceGroupId}
              onChange={(event) =>
                setSelectedServiceGroupId(event.target.value)
              }
              disabled={loading}
            >
              <option value="">No group/class</option>
              {activeServiceGroups.map((group) => (
                <option key={group.serviceGroupId} value={group.serviceGroupId}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Checking Status */}
      {checkingStatus && (
        <div className="notification notification--info">
          Checking registration...
        </div>
      )}

      {/* Already Registered Warning */}
      {registrationStatus?.isRegistered && (
        <div className="notification notification--warning">
          Already registered on{" "}
          {new Date(registrationStatus.registeredDateTime!).toLocaleString()}
        </div>
      )}

      {/* Register Button */}
      <div className="form-group">
        <button
          className="form-submit-button form-submit-button--standard"
          onClick={handleRegister}
          disabled={
            loading ||
            !serviceInstanceId ||
            !selectedParty ||
            registrationStatus?.isRegistered
          }
        >
          <HowToRegIcon fontSize="small" />
          {loading ? "Checking in..." : "Check In"}
        </button>
      </div>
    </div>
  );
}
